import { useState, useEffect, useCallback } from 'react';
import api from '../services/api';

export interface Promocao {
  id: number;
  nome: string;
  descricao: string;
  tipo: 'percentual' | 'valor_fixo' | 'frete_gratis';
  valor: number;
  codigo: string;
  data_inicio: string;
  data_fim: string;
  uso_maximo: number;
  uso_atual: number;
  valor_minimo: number;
  ativo: boolean;
  aplicavel_em: 'todos' | 'categorias' | 'produtos';
  itens_aplicaveis: string[];
}

export const usePromocoes = (empresaId?: number) => {
  const [promocoes, setPromocoes] = useState<Promocao[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchPromocoes = useCallback(async () => {
    try {
      // Usar rota pública para promoções
      const url = empresaId ? `/api/promocoes/public?empresa_id=${empresaId}` : '/api/promocoes/public';
      const response = await api.get(url);
      // Filtrar apenas promoções ativas e válidas
      const now = new Date();
      const promocoesAtivas = response.data.filter((promo: Promocao) => {
        const inicio = new Date(promo.data_inicio);
        const fim = new Date(promo.data_fim);
        return promo.ativo && now >= inicio && now <= fim && promo.uso_atual < promo.uso_maximo;
      });
      setPromocoes(promocoesAtivas);
    } catch (error) {
      console.error('Erro ao carregar promoções:', error);
      setPromocoes([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPromocoes();
  }, [fetchPromocoes]);

  // Verificar se uma promoção se aplica a um produto
  const isPromocaoAplicavel = useCallback((promocao: Promocao, produtoNome: string, categoriaNome: string): boolean => {
    if (promocao.aplicavel_em === 'todos') {
      return true;
    }
    
    if (promocao.aplicavel_em === 'categorias') {
      return promocao.itens_aplicaveis.some(item => 
        categoriaNome.toLowerCase().includes(item.toLowerCase()) || 
        item.toLowerCase().includes(categoriaNome.toLowerCase())
      );
    }
    
    if (promocao.aplicavel_em === 'produtos') {
      return promocao.itens_aplicaveis.some(item => 
        produtoNome.toLowerCase().includes(item.toLowerCase()) || 
        item.toLowerCase().includes(produtoNome.toLowerCase())
      );
    }
    
    return false;
  }, []);

  // Calcular preço com desconto de promoção
  const calcularPrecoComDesconto = useCallback((precoOriginal: number, promocao: Promocao): number => {
    if (promocao.tipo === 'percentual') {
      const desconto = (precoOriginal * promocao.valor) / 100;
      return Math.max(0, precoOriginal - desconto);
    } else {
      return Math.max(0, precoOriginal - promocao.valor);
    }
  }, []);

  // Encontrar melhor promoção para um produto
  const encontrarMelhorPromocao = useCallback((produtoNome: string, categoriaNome: string, preco: number): Promocao | null => {
    let melhorPromocao: Promocao | null = null;
    let maiorDesconto = 0;

    promocoes.forEach(promocao => {
      if (isPromocaoAplicavel(promocao, produtoNome, categoriaNome)) {
        const precoComDesconto = calcularPrecoComDesconto(preco, promocao);
        const desconto = preco - precoComDesconto;
        
        if (desconto > maiorDesconto) {
          maiorDesconto = desconto;
          melhorPromocao = promocao;
        }
      }
    });

    return melhorPromocao;
  }, [promocoes, isPromocaoAplicavel, calcularPrecoComDesconto]);

  // Validar cupom de desconto
  const validarCupom = useCallback(async (codigo: string, valorTotal: number): Promise<{ valido: boolean; promocao?: Promocao; mensagem?: string }> => {
    try {
      const promocao = promocoes.find(p => p.codigo.toUpperCase() === codigo.toUpperCase());
      
      if (!promocao) {
        return { valido: false, mensagem: 'Cupom não encontrado' };
      }

      if (!promocao.ativo) {
        return { valido: false, mensagem: 'Cupom inativo' };
      }

      const now = new Date();
      const inicio = new Date(promocao.data_inicio);
      const fim = new Date(promocao.data_fim);

      if (now < inicio) {
        return { valido: false, mensagem: 'Cupom ainda não está válido' };
      }

      if (now > fim) {
        return { valido: false, mensagem: 'Cupom expirado' };
      }

      if (promocao.uso_atual >= promocao.uso_maximo) {
        return { valido: false, mensagem: 'Cupom esgotado' };
      }

      if (valorTotal < promocao.valor_minimo) {
        const valorFaltante = promocao.valor_minimo - valorTotal;
        return { 
          valido: false, 
          mensagem: `Este cupom é válido apenas para pedidos acima de R$ ${Number(promocao.valor_minimo).toFixed(2)}. Faltam R$ ${valorFaltante.toFixed(2)} para usar este cupom.` 
        };
      }

      return { valido: true, promocao };
    } catch (error) {
      console.error('Erro ao validar cupom:', error);
      return { valido: false, mensagem: 'Erro ao validar cupom' };
    }
  }, [promocoes]);

  // Calcular desconto do cupom
  const calcularDescontoCupom = useCallback((valorTotal: number, promocao: Promocao): number => {
    if (promocao.tipo === 'percentual') {
      return (valorTotal * promocao.valor) / 100;
    } else if (promocao.tipo === 'frete_gratis') {
      return 0; // Frete grátis não dá desconto no valor, apenas no frete
    } else {
      return Math.min(promocao.valor, valorTotal);
    }
  }, []);

  // Buscar promoção de frete grátis ativa
  const getFreteGratisPromocao = useCallback((): Promocao | null => {
    const now = new Date();
    return promocoes.find((promo: Promocao) => {
      if (promo.tipo !== 'frete_gratis' || !promo.ativo) return false;
      const inicio = new Date(promo.data_inicio);
      const fim = new Date(promo.data_fim);
      return now >= inicio && now <= fim && promo.uso_atual < promo.uso_maximo;
    }) || null;
  }, [promocoes]);

  // Verificar se tem direito a frete grátis baseado no valor do pedido
  const temDireitoFreteGratis = useCallback((valorTotal: number): boolean => {
    const freteGratisPromo = getFreteGratisPromocao();
    if (!freteGratisPromo) return false;
    return valorTotal >= freteGratisPromo.valor_minimo;
  }, [getFreteGratisPromocao]);

  // Calcular valor faltante para frete grátis
  const valorFaltanteFreteGratis = useCallback((valorTotal: number): number => {
    const freteGratisPromo = getFreteGratisPromocao();
    if (!freteGratisPromo || valorTotal >= freteGratisPromo.valor_minimo) return 0;
    return freteGratisPromo.valor_minimo - valorTotal;
  }, [getFreteGratisPromocao]);

  return {
    promocoes,
    loading,
    fetchPromocoes,
    isPromocaoAplicavel,
    calcularPrecoComDesconto,
    encontrarMelhorPromocao,
    validarCupom,
    calcularDescontoCupom,
    getFreteGratisPromocao,
    temDireitoFreteGratis,
    valorFaltanteFreteGratis
  };
};

